import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { ChevronUp } from "lucide-react";
import { Fragment, useState } from "react";
import { useSelector } from "react-redux";

import SpendSummaryItem, {
  withOopLongLabel,
} from "@/pages/components/spend-summary/spend-summary-item";

import { formatCurrency } from "@/lib/cost-util";
import type {
  SpendSummaryNetwork,
  SpendSummaryRemaining,
  SpendSummaryTier,
} from "@/model/spend-summary";
import { SPEND } from "@/constants";
import { lowerCaseComparison } from "@/lib/string-util";
import type { RootState } from "@/store/store";
import { NoDataAvailable } from "../empty";

const SpendSummaryForNetwork = ({
  networkData,
  showFamilyDentalOop = false,
}: {
  networkData: SpendSummaryNetwork;
  showFamilyDentalOop?: boolean;
}) => {
  const { activeCoverage } = useSelector(
    (state: RootState) => state.planCoverageInfo,
  );
  const [openTiers, setOpenTiers] = useState<{ [tierType: string]: boolean }>(
    () =>
      Object.fromEntries(networkData.data.map((tier) => [tier.tierType, true])),
  );

  if (!networkData?.data?.[0]?.accumulator)
    return (
      <NoDataAvailable
        title="Not Covered by your Plan"
        message={networkData?.data?.[0]?.message}
      />
    );

  const handleOpenChange = (tierType: string, open: boolean) => {
    setOpenTiers((prev) => ({ ...prev, [tierType]: open }));
  };

  const renderRemaining = (remaining: SpendSummaryRemaining) => {
    return (
      <span className="text-sm font-semibold text-gray-600">
        {withOopLongLabel(remaining.label, activeCoverage?.claimCoverageCode)}
        <span className="text-ms ml-2 font-bold text-black">
          {formatCurrency(remaining.value)}
        </span>
      </span>
    );
  };

  const renderSpendBarChart = (tier: SpendSummaryTier) => {
    const oopMax =
      tier?.accumulator.length === 1
        ? tier?.accumulator?.[0]?.OOPMax
        : tier?.accumulator?.find((acc) =>
            lowerCaseComparison(acc.type, "family"),
          )?.OOPMax;
    const dedLimit =
      tier?.accumulator.length === 1
        ? tier?.accumulator?.[0]?.deductibleLimit
        : tier?.accumulator?.find((acc) =>
            lowerCaseComparison(acc.type, "family"),
          )?.deductibleLimit;

    function getValidRemaining(
      remaining: SpendSummaryRemaining[],
      oopMax: number,
      dedLimit: number,
    ) {
      return remaining.filter((item) => {
        const label = item?.label?.toLowerCase() || "";
        return (
          (label.includes("deductible") &&
            dedLimit > 0 &&
            dedLimit < SPEND.MAXIMUM) ||
          (label.includes("out-of-pocket") &&
            oopMax > 0 &&
            oopMax < SPEND.MAXIMUM) ||
          label.includes("coinsurance")
        );
      });
    }

    const validRemaining = getValidRemaining(
      tier?.remaining || [],
      oopMax ?? 0,
      dedLimit ?? 0,
    );

    return (
      <>
        <div className="grid w-full grid-cols-1 items-center justify-between gap-5 motion-translate-y-in-25 motion-duration-[0.5s] @[640px]:grid-cols-1 @[768px]:grid-cols-2 @[1280px]:grid-cols-3">
          {/*media queries based on container */}
          {tier.accumulator.map((accumulator, index) => (
            <SpendSummaryItem
              planType={networkData.PTPlanBenefitType}
              summary={accumulator}
              showFamilyDentalOop={showFamilyDentalOop}
              key={accumulator.memberId + index}
            />
          ))}
        </div>

        {validRemaining.length > 0 && (
          <div className="mt-4 flex flex-row items-center justify-center gap-4 bg-slate-50 p-4">
            {validRemaining
              .slice()
              .reverse()
              .map((remaining, index) => (
                <Fragment key={`${remaining.label}-${index}`}>
                  {index > 0 && <div className="h-4 w-px bg-gray-300" />}
                  <div className="flex items-center justify-center">
                    {renderRemaining(remaining)}
                  </div>
                </Fragment>
              ))}
          </div>
        )}
      </>
    );
  };

  const renderCollapsibleWrapper = () => {
    return networkData.data.map((tier: SpendSummaryTier) => (
      <Collapsible
        key={tier.tierType}
        open={openTiers[tier.tierType]}
        onOpenChange={(open) => handleOpenChange(tier.tierType, open)}
      >
        <div className="mt-6 w-full @container">
          {" "}
          {/* container for displaying in cost estimator */}
          <CollapsibleTrigger asChild>
            <div className="flex w-full flex-col items-center justify-between border-b bg-gray-100 px-3 py-1">
              <div className="flex w-full items-center justify-between">
                <Label className="text-sm font-semibold text-primary">
                  {tier.tierLabel}
                </Label>
                <Button variant="link" className="p-0">
                  <ChevronUp
                    className={`transition-transform duration-200 ${!openTiers[tier.tierType] ? "rotate-180" : ""}`}
                  />
                </Button>
              </div>
            </div>
          </CollapsibleTrigger>
          <CollapsibleContent data-state="open" className="mt-4">
            {renderSpendBarChart(tier)}
          </CollapsibleContent>
        </div>
      </Collapsible>
    ));
  };

  return networkData?.data?.length > 1 ? (
    renderCollapsibleWrapper()
  ) : (
    <div className="mt-6">{renderSpendBarChart(networkData?.data[0])}</div>
  );
};

export default SpendSummaryForNetwork;
